import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "components/ui/Select";
import Input from "components/ui/Input";
import { SelectBrandAndSubbrands } from "components/ui/selectors";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "components/ui/Table";
import { TrashIcon } from "@heroicons/react/24/outline";
import { ActionButton } from "components/ui/Table";
import { useAllSchemeMasters } from "http-hooks/schemeMaster/useSchemeMaster";
import { useAllSchemeGiftItems } from "http-hooks/schmitemaster/useSchemeGiftItem";
import {
  useCreateSchemeDetail,
  useUpdateSchemeDetail,
  useDeleteSchemeDetail,
  useSchemeDetailsBySchemeId,
} from "http-hooks/schemedetail/useSchemeDetail";
import useAuth from "hooks/useAuth";
import ItemSelectSizeGrouped from "components/item/ItemSelectSizeGrouped";
import DeleteConfirmation from "components/ui/DeleteConfirmation";
import Modal from "components/ui/modals";

export default function SchemeDetail() {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { agttp } = useAuth();

  // Selectors for adding new items (used in both create & edit)
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedGiftItems, setSelectedGiftItems] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // Tables (in edit mode, populated from DB; in create mode, built from selections)
  const [giftItemsTable, setGiftItemsTable] = useState([]);
  const [schemeItemsTable, setSchemeItemsTable] = useState([]);

  // Edit mode: sections to add new items
  const [showAddGifts, setShowAddGifts] = useState(false);
  const [showAddItems, setShowAddItems] = useState(false);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { mutateAsync: createDetail, isPending: isCreating } = useCreateSchemeDetail();
  const { mutateAsync: updateDetail, isPending: isUpdating } = useUpdateSchemeDetail();
  const { mutateAsync: deleteDetail, isPending: isDeleting } = useDeleteSchemeDetail();

  // Fetch existing data in edit mode
  const { data: existingData, isLoading: isLoadingExisting } =
    useSchemeDetailsBySchemeId(id, isEditMode);

  // Fetch schemes for create mode selector
  const { isLoading: isSchemesLoading, data: { data: schemes } = {} } =
    useAllSchemeMasters({ agttp, ag_status: 1, noPagination: true });

  // Fetch all gift items
  const { isLoading: isGiftItemsLoading, data: { data: giftItems } = {} } =
    useAllSchemeGiftItems({ noPagination: true }, true);

  // In edit mode, find scheme from list to get vendor
  const schemeInfo = useMemo(() => {
    if (!schemes || !id) return null;
    return schemes.find((s) => String(s.schemeid) === String(id));
  }, [schemes, id]);

  const schemeOptions = useMemo(() => {
    if (!schemes) return [];
    return schemes.map((s) => ({
      value: s.schemeid,
      label: s.sch_name,
      schmtp: s.schmtp,
      vendor: s.vendor,
    }));
  }, [schemes]);

  const giftItemOptions = useMemo(() => {
    if (!giftItems) return [];
    return giftItems.map((item) => ({
      value: item.schmitemId,
      label: `${item.schmitemname} | Rs. ${item.schm_value}`,
      name: item.schmitemname,
      value_amt: item.schm_value,
    }));
  }, [giftItems]);

  const schemeVendor = useMemo(() => {
    if (isEditMode) return schemeInfo?.vendor || null;
    return selectedScheme?.vendor || null;
  }, [isEditMode, schemeInfo, selectedScheme]);

  // Populate tables from existing data in edit mode
  useEffect(() => {
    if (!isEditMode || !existingData) return;

    setGiftItemsTable(
      (existingData.giftDetails || []).map((g) => ({
        itm: g.schitmid,
        name: g.itemName || `Item ${g.schitmid}`,
        value_amt: "",
        points: g.elgpoints,
        remark: g.remark || "",
      }))
    );

    setSchemeItemsTable(
      (existingData.schemeDetails || []).map((d) => {
        let itmmainid;
        if (d.itemid && d.itemid.trim().length > 0) {
          itmmainid = `${d.itemid}$%$${d.shp}$%$${d.clt}`;
        } else {
          itmmainid = d.brandid;
        }
        return {
          itmmainid,
          displayName: d.displayName || itmmainid,
          small_point: d.smallpoint,
          big_point: d.bigpoint,
          remarks: d.remark || "",
        };
      })
    );
  }, [isEditMode, existingData]);

  // Determine detail type from the table data
  const detailType = useMemo(() => {
    if (schemeItemsTable.length > 0) {
      return schemeItemsTable[0].itmmainid.includes("$%$") ? "item" : "brand";
    }
    if (selectedItems.length > 0) return "item";
    if (selectedBrands.length > 0) return "brand";
    return null;
  }, [schemeItemsTable, selectedItems, selectedBrands]);

  const handleGiftItemChange = (index, field, value) => {
    const t = [...giftItemsTable];
    t[index][field] = value;
    setGiftItemsTable(t);
  };

  const handleSchemeItemChange = (index, field, value) => {
    const t = [...schemeItemsTable];
    t[index][field] = value;
    setSchemeItemsTable(t);
  };

  const handleRemoveGift = (index) => {
    setGiftItemsTable((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveSchemeItem = (index) => {
    setSchemeItemsTable((prev) => prev.filter((_, i) => i !== index));
  };

  // Create mode: scheme change resets everything
  const handleSchemeChange = (scheme) => {
    setSelectedScheme(scheme);
    setSelectedGiftItems([]);
    setSelectedBrands([]);
    setSelectedItems([]);
    setGiftItemsTable([]);
    setSchemeItemsTable([]);
  };

  // Build tables from selections (create mode) or add to existing (edit mode)
  const handleProceed = () => {
    // Build gift items from selection
    if (selectedGiftItems.length > 0) {
      const newGifts = selectedGiftItems.map((item) => ({
        itm: item.value,
        name: item.name,
        value_amt: item.value_amt,
        points: 0,
        remark: "",
      }));
      if (isEditMode) {
        setGiftItemsTable((prev) => [...prev, ...newGifts]);
      } else {
        setGiftItemsTable(newGifts);
      }
      setSelectedGiftItems([]);
    }

    // Build scheme items from selection
    let newSchemeItems = [];
    if (selectedItems.length > 0) {
      newSchemeItems = selectedItems.map((item) => {
        const val = item.value || "";
        const itemId = val.substring(0, 10);
        const shp = val.substring(10, 13);
        const clt = val.substring(13, 16);
        return {
          itmmainid: `${itemId}$%$${shp}$%$${clt}`,
          displayName: item.label,
          small_point: 1.0,
          big_point: 1.0,
          remarks: "",
        };
      });
    } else if (selectedBrands.length > 0) {
      newSchemeItems = selectedBrands.map((brand) => ({
        itmmainid: brand.value,
        displayName: brand.label,
        small_point: 1.0,
        big_point: 1.0,
        remarks: "",
      }));
    }

    if (newSchemeItems.length > 0) {
      if (isEditMode) {
        setSchemeItemsTable((prev) => [...prev, ...newSchemeItems]);
      } else {
        setSchemeItemsTable(newSchemeItems);
      }
      setSelectedBrands([]);
      setSelectedItems([]);
    }

    if (isEditMode) {
      setShowAddGifts(false);
      setShowAddItems(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const schemeId = isEditMode ? parseInt(id) : selectedScheme?.value;
      if (!schemeId) {
        toast.error("Please select a scheme");
        return;
      }
      if (giftItemsTable.length === 0) {
        toast.error("Please add gift items");
        return;
      }
      if (schemeItemsTable.length === 0) {
        toast.error("Please add brands or items");
        return;
      }

      const giftArr = giftItemsTable.map((item) => ({
        itm: item.itm,
        points: parseFloat(item.points) || 0,
        remark: item.remark || "",
      }));
      const schArr = schemeItemsTable.map((item) => ({
        itmmainid: item.itmmainid,
        small_point: parseFloat(item.small_point) || 0,
        big_point: parseFloat(item.big_point) || 0,
        remarks: item.remarks || "",
      }));

      if (isEditMode) {
        await updateDetail({
          id,
          data: {
            schememainid: schemeId,
            schemedetail_type: detailType,
            giftitemarr_prev: giftArr,
            giftitemarr: [],
            schdetail_prev: schArr,
            schdetail: [],
          },
        });
      } else {
        await createDetail({
          schememainid: schemeId,
          schemedetail_type: detailType,
          giftitemarr: giftArr,
          schdetail: schArr,
        });
      }
      navigate("/scheme-detail-edit");
    } catch (error) {
      console.error("Error saving scheme detail:", error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDetail(id);
      navigate("/scheme-detail-edit");
    } catch (error) {
      console.error("Error deleting scheme detail:", error);
    }
  };

  const canProceed =
    selectedGiftItems.length > 0 ||
    selectedBrands.length > 0 ||
    selectedItems.length > 0;

  if (isEditMode && isLoadingExisting) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  const isSaving = isCreating || isUpdating;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <h1 className="font-semibold leading-6 text-3xl text-gray-900 text-center">
        {isEditMode ? "Scheme Detail Edit" : "Scheme Detail"}
      </h1>

      {/* Edit mode: show scheme name */}
      {isEditMode && schemeInfo && (
        <h2 className="text-xl font-bold text-gray-900 text-center mt-4">
          Scheme Name: {schemeInfo.sch_name}
        </h2>
      )}

      {/* Create mode: show selectors for scheme, gift items, brands, items */}
      {!isEditMode && (
        <div className="max-w-md mx-auto mt-6 space-y-4">
          <Select
            label="Scheme *"
            value={selectedScheme}
            onChange={handleSchemeChange}
            options={schemeOptions}
            placeholder="Select scheme"
            isClearable={true}
            isLoading={isSchemesLoading}
            isSearchable={true}
          />

          <Select
            label="Gift Item *"
            value={selectedGiftItems}
            onChange={setSelectedGiftItems}
            options={giftItemOptions}
            placeholder="Select gift items"
            isClearable={true}
            isMulti={true}
            isSearchable={true}
            isLoading={isGiftItemsLoading}
          />

          <SelectBrandAndSubbrands
            label="Brand/Sub-brand *"
            value={selectedBrands}
            onChange={(val) => {
              setSelectedBrands(val || []);
              setSelectedItems([]);
            }}
            isMulti={true}
            showAddButton={false}
          />

          {selectedBrands.length > 0 && (
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                Item Name
              </label>
              <ItemSelectSizeGrouped
                value={selectedItems}
                onChange={(val) =>
                  setSelectedItems(val ? (Array.isArray(val) ? val : [val]) : [])
                }
                vendorId={schemeVendor}
                isMulti={true}
                requireVendor={false}
                placeholder="Select items (optional - leave empty for brand-level scheme)"
              />
            </div>
          )}

          {/* Proceed Button */}
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={handleProceed}
              disabled={!selectedScheme || !canProceed}
              className="rounded-md bg-indigo-600 px-8 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Proceed
            </button>
          </div>
        </div>
      )}

      {/* Gift Items Table */}
      {giftItemsTable.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4 text-center">
            Gift Items
          </h2>
          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <THead>
                  <TR>
                    <TH first style={{ width: "40%" }}>Item Name</TH>
                    <TH style={{ width: "15%" }}>No of Points</TH>
                    <TH style={{ width: "35%" }}>Remarks</TH>
                    <TH last style={{ width: "10%" }}></TH>
                  </TR>
                </THead>
                <TBody>
                  {giftItemsTable.map((item, index) => (
                    <TR key={index}>
                      <TD first>
                        {item.name}
                        {item.value_amt ? ` | Rs. ${item.value_amt}` : ""}
                      </TD>
                      <TD>
                        <Input
                          type="number"
                          value={item.points}
                          onChange={(e) =>
                            handleGiftItemChange(index, "points", e.target.value)
                          }
                          showLabel={false}
                          min={0}
                          step={1}
                        />
                      </TD>
                      <TD>
                        <Input
                          type="text"
                          value={item.remark}
                          onChange={(e) =>
                            handleGiftItemChange(index, "remark", e.target.value)
                          }
                          showLabel={false}
                          maxLength={40}
                        />
                      </TD>
                      <TD last>
                        <ActionButton
                          onClick={() => handleRemoveGift(index)}
                          variant="danger"
                          title="Remove"
                          className="mr-0"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </ActionButton>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </div>

          {/* Edit mode: Add Gifts button */}
          {isEditMode && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowAddGifts(!showAddGifts)}
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Add Gifts
              </button>
            </div>
          )}
        </div>
      )}

      {/* Edit mode: Add gift items selector */}
      {isEditMode && showAddGifts && (
        <div className="max-w-md mx-auto mt-4 space-y-4">
          <Select
            label="Gift Item *"
            value={selectedGiftItems}
            onChange={setSelectedGiftItems}
            options={giftItemOptions}
            placeholder="Select gift items"
            isClearable={true}
            isMulti={true}
            isSearchable={true}
            isLoading={isGiftItemsLoading}
          />
        </div>
      )}

      {/* Scheme Items Table */}
      {schemeItemsTable.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4 text-center">
            {detailType === "brand" ? "Brand/Sub-brand Details" : "Item Details"}
          </h2>
          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <THead>
                  <TR>
                    <TH first style={{ width: "40%" }}>
                      {detailType === "brand" ? "Brand/Sub-brand Name" : "Item Name"}
                    </TH>
                    <TH style={{ width: "12%" }}>Small Size Points</TH>
                    <TH style={{ width: "12%" }}>Big Size Points</TH>
                    <TH style={{ width: "26%" }}>Remarks</TH>
                    <TH last style={{ width: "10%" }}></TH>
                  </TR>
                </THead>
                <TBody>
                  {schemeItemsTable.map((item, index) => (
                    <TR key={index}>
                      <TD first>{item.displayName}</TD>
                      <TD>
                        <Input
                          type="number"
                          value={item.small_point}
                          onChange={(e) =>
                            handleSchemeItemChange(index, "small_point", e.target.value)
                          }
                          showLabel={false}
                          min={0}
                          step={0.00001}
                        />
                      </TD>
                      <TD>
                        <Input
                          type="number"
                          value={item.big_point}
                          onChange={(e) =>
                            handleSchemeItemChange(index, "big_point", e.target.value)
                          }
                          showLabel={false}
                          min={0}
                          step={0.00001}
                        />
                      </TD>
                      <TD>
                        <Input
                          type="text"
                          value={item.remarks}
                          onChange={(e) =>
                            handleSchemeItemChange(index, "remarks", e.target.value)
                          }
                          showLabel={false}
                          maxLength={40}
                        />
                      </TD>
                      <TD last>
                        <ActionButton
                          onClick={() => handleRemoveSchemeItem(index)}
                          variant="danger"
                          title="Remove"
                          className="mr-0"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </ActionButton>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </div>

          {/* Edit mode: Add Items button */}
          {isEditMode && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowAddItems(!showAddItems)}
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Add Items
              </button>
            </div>
          )}
        </div>
      )}

      {/* Edit mode: Add scheme items selectors */}
      {isEditMode && showAddItems && (
        <div className="max-w-md mx-auto mt-4 space-y-4">
          <SelectBrandAndSubbrands
            label="Brand/Sub-brand *"
            value={selectedBrands}
            onChange={(val) => {
              setSelectedBrands(val || []);
              setSelectedItems([]);
            }}
            isMulti={true}
            showAddButton={false}
          />

          {selectedBrands.length > 0 && (
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                Item Name
              </label>
              <ItemSelectSizeGrouped
                value={selectedItems}
                onChange={(val) =>
                  setSelectedItems(val ? (Array.isArray(val) ? val : [val]) : [])
                }
                vendorId={schemeVendor}
                isMulti={true}
                requireVendor={false}
                placeholder="Select items (optional)"
              />
            </div>
          )}
        </div>
      )}

      {/* Edit mode: Proceed for adding new items */}
      {isEditMode && (showAddGifts || showAddItems) && canProceed && (
        <div className="flex justify-center mt-4">
          <button
            type="button"
            onClick={handleProceed}
            className="rounded-md bg-indigo-600 px-8 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            Proceed
          </button>
        </div>
      )}

      {/* Action Buttons */}
      {(schemeItemsTable.length > 0 || isEditMode) && (
        <div className="flex items-center justify-center gap-x-4 mt-8 mb-8">
          {isEditMode && (
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="rounded-md bg-red-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="rounded-md bg-indigo-600 px-12 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "Submitting..." : "Submit"}
          </button>
        </div>
      )}

      {/* Delete Modal */}
      <Modal open={showDeleteModal} setOpen={setShowDeleteModal}>
        <DeleteConfirmation
          title="Delete Scheme Detail"
          description="Are you sure you want to delete this scheme detail? This action cannot be undone."
          onDelete={handleDelete}
          onClose={() => setShowDeleteModal(false)}
          isLoading={isDeleting}
        />
      </Modal>
    </div>
  );
}
